/**
 * HeroMediaCarousel — Auto-cycling slideshow in Sean's hero frame.
 *
 * Rotation order (shuffled on load, then stable loop):
 *   1. Sean photo        → 3 seconds, cartoon doodles + blue band visible
 *   2. YouTube clip      → autoplay muted, ~14 seconds, doodles fade
 *   3. Instagram reel    → embed card, ~8 seconds, doodles fade
 *   4. Studio post media → whatever Sean just blasted (newest first)
 *   → loop forever
 *
 * Why not longer per slide? Hero is top-of-page attention real-estate — the
 * goal is "flash the taste, pull them into the real sections below."
 *
 * Instagram caveat: web embeds don't auto-play; the card shows the first
 * frame + CTA. Tap to open full reel in the IG app.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Volume2, VolumeX } from "lucide-react";
import { seanListPosts, getChannelVideos, mediaSrc } from "@/lib/api";

/* ============ Static base library ============ */
const PHOTO_LIBRARY = [
  "/sean-portrait-new.jpg",
  "/sean-hero-new.jpg",
  "/sean-lab-new.jpg",
  "/sean-ted-new.jpg",
  "/sean-photo-new.jpg",
];

// Only use YouTube IDs from Sean's OWN channel. News channels (CNN / WGN / FOX /
// TED / Jennifer Hudson) typically block off-site embedding ("Video unavailable"),
// so we never show them in the auto-rotating hero. Sean can add more of his own
// uploads at any time via the Post Studio (`/#sean`) — they get picked up here
// automatically.
// Fallback only — the live list is pulled from Sean's channel feed at runtime.
const YOUTUBE_LIBRARY = [
  { id: "SfpITrSZclU", title: "Co-hosting the BeeWell Spelling Bee" },
];

// Instagram reel URLs — seed list is empty; populated at runtime by any Studio
// posts whose media_url is an Instagram URL. Admin can also hard-code known
// reels here if desired.
const INSTAGRAM_LIBRARY = [];

/* ============ Timing (ms) ============ */
const DURATION = { photo: 3000, instagram: 8000, link: 6000 };

/* ============ Helpers ============ */
const extractYouTubeId = (url = "") => {
  const m =
    url.match(/[?&]v=([^&]+)/) ||
    url.match(/youtu\.be\/([^?&]+)/) ||
    url.match(/\/shorts\/([^?&]+)/) ||
    url.match(/\/embed\/([^?&]+)/);
  return m ? m[1] : null;
};

const detectKind = (url) => {
  if (!url) return null;
  if (/youtu\.be\/|youtube\.com\//i.test(url)) return "youtube";
  if (/instagram\.com\/(reel|p|reels)\//i.test(url)) return "instagram";
  return "link";
};

// Fisher–Yates shuffle (so the order isn't predictable on every load)
const shuffle = (arr) => {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

/* ============ Build the master playlist ============ */
function buildPlaylist(studioPosts, channelVideos) {
  const photos = PHOTO_LIBRARY.map((src) => ({ kind: "photo", src }));
  const source = channelVideos?.length ? channelVideos : YOUTUBE_LIBRARY;
  const ytClips = source.map((v) => ({ kind: "youtube", id: v.id, title: v.title }));
  const igClips = INSTAGRAM_LIBRARY.map((url) => ({ kind: "instagram", url }));

  // Add any Studio-posted media to the live mix
  (studioPosts || []).forEach((p) => {
    if (p.upload_kind === "image") photos.unshift({ kind: "photo", src: mediaSrc(p.upload_url) });
    if (p.upload_kind === "video") ytClips.unshift({ kind: "video", src: mediaSrc(p.upload_url), id: p.id, title: p.caption?.slice(0, 60) || "Fresh from the lab" });
    const kind = detectKind(p.media_url);
    if (kind === "youtube") {
      const id = extractYouTubeId(p.media_url);
      if (id) ytClips.unshift({ kind: "youtube", id, title: p.caption?.slice(0, 60) || "Fresh from the lab" });
    } else if (kind === "instagram") {
      igClips.unshift({ kind: "instagram", url: p.media_url });
    }
  });

  // Interleave: photo, yt, ig, photo, yt, ig … falls back gracefully if a bucket is empty
  const p = shuffle(photos);
  const y = shuffle(ytClips);
  const i = shuffle(igClips);
  const out = [];
  const maxLen = Math.max(p.length, y.length, i.length);
  for (let k = 0; k < maxLen; k++) {
    if (p[k]) out.push(p[k]);
    if (y[k]) out.push(y[k]);
    if (i[k]) out.push(i[k]);
  }
  return out.length ? out : [{ kind: "photo", src: PHOTO_LIBRARY[0] }];
}

/* ============ Instagram embed script loader ============ */
let IG_SCRIPT_PROMISE = null;
const loadInstagramScript = () => {
  if (IG_SCRIPT_PROMISE) return IG_SCRIPT_PROMISE;
  IG_SCRIPT_PROMISE = new Promise((resolve) => {
    if (window.instgrm?.Embeds) { resolve(); return; }
    const s = document.createElement("script");
    s.async = true;
    s.src = "https://www.instagram.com/embed.js";
    s.onload = () => resolve();
    s.onerror = () => resolve();
    document.body.appendChild(s);
  });
  return IG_SCRIPT_PROMISE;
};

/* ============ YouTube IFrame API loader ============ */
let YT_API_PROMISE = null;
const loadYouTubeApi = () => {
  if (YT_API_PROMISE) return YT_API_PROMISE;
  YT_API_PROMISE = new Promise((resolve) => {
    if (window.YT?.Player) { resolve(window.YT); return; }
    const prev = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => { prev?.(); resolve(window.YT); };
    const s = document.createElement("script");
    s.src = "https://www.youtube.com/iframe_api";
    s.async = true;
    document.body.appendChild(s);
  });
  return YT_API_PROMISE;
};

/* ============ Main component ============ */
export default function HeroMediaCarousel({ photoSrc, children }) {
  const [studioPosts, setStudioPosts] = useState([]);
  const [channelVideos, setChannelVideos] = useState([]);
  const [soundOn, setSoundOn] = useState(true);
  const [index, setIndex] = useState(0);
  const next = useCallback(() => setIndex((n) => n + 1), []);

  // Browsers block sound until the visitor interacts once — so the first tap/click/key anywhere turns it on.
  const userChoseRef = useRef(false);
  useEffect(() => {
    const evts = ["click", "touchend", "keydown"];
    const enable = (e) => {
      if (e.target?.closest?.('[data-testid="hero-sound-toggle"]')) return;
      if (!userChoseRef.current) setSoundOn(true);
      evts.forEach((n) => document.removeEventListener(n, enable, true));
    };
    evts.forEach((n) => document.addEventListener(n, enable, true));
    return () => evts.forEach((n) => document.removeEventListener(n, enable, true));
  }, []);
  const [paused, setPaused] = useState(false);
  const timerRef = useRef(null);

  // Fetch Studio posts once + poll gently so new posts enter the rotation
  useEffect(() => {
    let alive = true;
    const load = () => seanListPosts(10)
      .then((d) => {
        if (!alive) return;
        const items = d.items || [];
        // Keep the same array if nothing changed so the playlist doesn't reshuffle mid-video
        setStudioPosts((prev) => (prev.map((p) => p.id).join() === items.map((p) => p.id).join() ? prev : items));
      })
      .catch(() => {});
    load();
    getChannelVideos(15).then((d) => { if (alive) setChannelVideos(d.items || []); }).catch(() => {});
    const id = setInterval(load, 120_000); // every 2 min
    return () => { alive = false; clearInterval(id); };
  }, []);

  // Build playlist whenever Studio posts / channel videos change; stable across re-renders
  const playlist = useMemo(() => buildPlaylist(studioPosts, channelVideos), [studioPosts, channelVideos]);
  const current = playlist[index % playlist.length];

  // Advance timer for photos / Instagram. YouTube slides advance themselves when the video ends.
  useEffect(() => {
    if (paused || current?.kind === "youtube" || current?.kind === "video") return;
    clearTimeout(timerRef.current);
    const dur = DURATION[current?.kind] || DURATION.photo;
    timerRef.current = setTimeout(next, dur);
    return () => clearTimeout(timerRef.current);
  }, [index, paused, current?.kind, next]);

  // Clamp index if playlist shrinks under us
  useEffect(() => {
    if (index >= playlist.length) setIndex(0);
  }, [playlist.length, index]);

  const isPhoto = current?.kind === "photo";
  // Photo slide = all the SVG doodles + blue band on; media slides fade them so content is readable
  const chromeVisible = isPhoto;

  return (
    <div
      className="absolute inset-0 w-full h-full"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onTouchStart={() => setPaused(true)}
      onTouchEnd={() => setTimeout(() => setPaused(false), 400)}
      data-testid="hero-media-carousel"
    >
      {/* ===== Active media slot ===== */}
      <div className="absolute inset-0" style={{ background: "#0e1230" }}>
        {current?.kind === "photo" && (
          <img
            key={current.src}
            src={current.src}
            alt="Sean the Science Kid"
            className="w-full h-full block"
            style={{ objectFit: "cover", objectPosition: "center top", animation: "heroFade 600ms ease-out" }}
            data-testid={`hero-slide-photo`}
          />
        )}
        {current?.kind === "youtube" && (
          <YouTubeSlide key={current.id} id={current.id} title={current.title} soundOn={soundOn} onDone={next} onSoundBlocked={() => setSoundOn(false)} />
        )}
        {current?.kind === "video" && (
          <UploadedVideoSlide key={current.id} src={current.src} soundOn={soundOn} onDone={next} onSoundBlocked={() => setSoundOn(false)} />
        )}
        {current?.kind === "instagram" && (
          <InstagramSlide url={current.url} />
        )}
      </div>

      {/* ===== Fallback / initial image so SVG doodles stay anchored to the frame ===== */}
      {/* Children = SVG doodles + blue band + pills from parent. They stay in DOM but fade on non-photo slides. */}
      <div
        className="absolute inset-0 pointer-events-none transition-opacity duration-500"
        style={{ opacity: chromeVisible ? 1 : 0 }}
      >
        {children}
      </div>

      {/* ===== Media-type pill in corner ===== */}
      <div
        className="absolute top-2 right-2 pill text-[0.6rem]"
        style={{
          background: current?.kind === "photo" ? "rgba(255,255,255,0.9)" : "rgba(14,18,48,0.85)",
          color: current?.kind === "photo" ? "var(--ink)" : "#fff",
          border: "2px solid var(--ink)",
          zIndex: 10,
        }}
        data-testid="hero-slide-kind"
      >
        {current?.kind === "photo" && "📸 photo"}
        {current?.kind === "youtube" && "▶ YouTube"}
        {current?.kind === "video" && "🎬 new from Sean"}
        {current?.kind === "instagram" && "📷 Instagram"}
      </div>

      {(current?.kind === "youtube" || current?.kind === "video") && (
        <SoundToggle on={soundOn} onToggle={() => { userChoseRef.current = true; setSoundOn((s) => !s); }} />
      )}

      {/* ===== Slide dots ===== */}
      <div
        className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1"
        style={{ zIndex: 10 }}
      >
        {playlist.slice(0, 24).map((_, i) => (
          <button
            key={i}
            onClick={() => setIndex(i)}
            aria-label={`Go to slide ${i + 1}`}
            className="w-1.5 h-1.5 rounded-full transition-all"
            style={{
              background: i === (index % playlist.length) ? "var(--sun)" : "rgba(255,255,255,0.45)",
              border: "1px solid var(--ink)",
              transform: i === (index % playlist.length) ? "scale(1.4)" : "scale(1)",
            }}
            data-testid={`hero-slide-dot-${i}`}
          />
        ))}
      </div>

      <style>{`
        @keyframes heroFade {
          from { opacity: 0; transform: scale(1.02); }
          to   { opacity: 1; transform: scale(1); }
        }
        @keyframes heroKenBurns {
          from { transform: scale(1.05); }
          to   { transform: scale(1.18); }
        }
      `}</style>
    </div>
  );
}

/* ============ Sound toggle ============ */
function SoundToggle({ on, onToggle }) {
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); onToggle(); }}
      className="absolute top-2 left-2 pill text-[0.65rem] inline-flex items-center gap-1.5 transition-transform hover:scale-105 active:scale-95"
      style={{ background: on ? "var(--sun)" : "rgba(14,18,48,0.85)", color: on ? "var(--ink)" : "#fff", border: "2px solid var(--ink)", zIndex: 11 }}
      aria-label={on ? "Mute video" : "Turn on sound"}
      data-testid="hero-sound-toggle"
      data-sound={on ? "on" : "off"}
    >
      {on ? <Volume2 size={14} /> : <VolumeX size={14} />}
      {on ? "sound on" : "tap for sound"}
    </button>
  );
}

/* ============ Uploaded video slide (from Sean's Studio) ============ */
function UploadedVideoSlide({ src, soundOn, onDone, onSoundBlocked }) {
  const ref = useRef(null);
  useEffect(() => {
    const v = ref.current;
    v.muted = !soundOn;
    v.play().catch(() => { v.muted = true; onSoundBlocked(); v.play().catch(() => onDone()); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => { if (ref.current) ref.current.muted = !soundOn; }, [soundOn]);
  return (
    <video
      ref={ref}
      src={src}
      playsInline
      onEnded={onDone}
      onError={onDone}
      className="absolute inset-0 w-full h-full"
      style={{ objectFit: "cover", animation: "heroFade 600ms ease-out" }}
      data-testid="hero-slide-video"
    />
  );
}

/* ============ YouTube slide ============ */
// Thumbnail poster always renders underneath; the player only fades in once it is
// actually PLAYING, so a blocked/unavailable embed never shows YouTube's error box.
const applySound = (p, on) => {
  try {
    if (on) { p.unMute(); p.setVolume(100); } else { p.mute(); }
  } catch {/* player not ready */}
};

function YouTubeSlide({ id, title, soundOn, onDone, onSoundBlocked }) {
  const hostRef = useRef(null);
  const playerRef = useRef(null);
  const soundRef = useRef(soundOn);
  const cbRef = useRef({ onDone, onSoundBlocked });
  cbRef.current = { onDone, onSoundBlocked };
  const [playing, setPlaying] = useState(false);

  useEffect(() => {
    soundRef.current = soundOn;
    if (playerRef.current) applySound(playerRef.current, soundOn);
  }, [soundOn]);

  useEffect(() => {
    let player;
    let cancelled = false;
    const timers = [];
    const later = (fn, ms) => timers.push(setTimeout(() => { if (!cancelled) fn(); }, ms));
    let started = false;
    loadYouTubeApi().then((YT) => {
      if (cancelled || !hostRef.current) return;
      player = new YT.Player(hostRef.current, {
        videoId: id,
        host: "https://www.youtube-nocookie.com",
        playerVars: { autoplay: 1, mute: soundRef.current ? 0 : 1, controls: 0, rel: 0, modestbranding: 1, playsinline: 1 },
        events: {
          onReady: (e) => {
            playerRef.current = e.target;
            applySound(e.target, soundRef.current);
            e.target.playVideo();
            // Browser refused to autoplay with sound → retry muted (visitor's first tap turns sound back on)
            later(() => {
              if (!started && soundRef.current) { e.target.mute(); e.target.playVideo(); cbRef.current.onSoundBlocked(); }
            }, 1500);
            // Never got going (blocked / bot-checked) → move on after showing the poster
            later(() => { if (!started) cbRef.current.onDone(); }, 9000);
          },
          onStateChange: (e) => {
            if (e.data === YT.PlayerState.PLAYING) { started = true; setPlaying(true); }
            if (e.data === YT.PlayerState.ENDED) cbRef.current.onDone();
          },
          onError: () => { setPlaying(false); later(() => cbRef.current.onDone(), 4000); },
        },
      });
    });
    return () => {
      cancelled = true;
      timers.forEach(clearTimeout);
      playerRef.current = null;
      try { player?.destroy(); } catch {/* ignore */}
    };
  }, [id]);

  return (
    <div className="absolute inset-0 overflow-hidden" data-testid="hero-slide-youtube" data-playing={playing ? "1" : "0"}>
      <img
        src={`https://i.ytimg.com/vi/${id}/hqdefault.jpg`}
        alt={title}
        className="absolute inset-0 w-full h-full"
        style={{ objectFit: "cover", animation: "heroKenBurns 14s ease-out forwards" }}
        data-testid="hero-slide-youtube-poster"
      />
      <div
        className="absolute inset-0 transition-opacity duration-700 [&>iframe]:w-full [&>iframe]:h-full"
        style={{ opacity: playing ? 1 : 0, pointerEvents: playing ? "auto" : "none" }}
      >
        <div ref={hostRef} />
      </div>
      {!playing && (
        <a
          href={`https://www.youtube.com/watch?v=${id}`}
          target="_blank"
          rel="noreferrer noopener"
          className="absolute inset-x-3 bottom-8 flex items-center gap-2 rounded-xl px-3 py-2 text-white text-xs sm:text-sm font-semibold"
          style={{ background: "rgba(14,18,48,0.8)", border: "2px solid var(--ink)", zIndex: 5 }}
          data-testid="hero-slide-youtube-watch-link"
        >
          <span className="shrink-0 w-7 h-7 rounded-full flex items-center justify-center" style={{ background: "var(--red)" }}>▶</span>
          <span className="truncate">{title}</span>
        </a>
      )}
    </div>
  );
}

/* ============ Instagram slide ============ */
function InstagramSlide({ url }) {
  const containerRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    loadInstagramScript().then(() => {
      if (cancelled) return;
      try { window.instgrm?.Embeds?.process(); } catch {/* ignore */}
    });
    return () => { cancelled = true; };
  }, [url]);

  return (
    <div
      ref={containerRef}
      className="w-full h-full flex items-center justify-center"
      style={{ background: "#fafafa", overflow: "hidden" }}
    >
      <blockquote
        className="instagram-media"
        data-instgrm-permalink={url}
        data-instgrm-version="14"
        style={{
          background: "#FFF",
          border: 0,
          margin: "0 auto",
          maxWidth: "100%",
          minWidth: "280px",
          padding: 0,
          width: "100%",
        }}
      >
        <a href={url} target="_blank" rel="noreferrer noopener" style={{ color: "#000", textDecoration: "none" }}>
          View on Instagram
        </a>
      </blockquote>
    </div>
  );
}
